import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, computed, inject, signal } from '@angular/core';
import { PermissionService } from '../../permissions/permission.service';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  CrmFeature,
  LeadStatusesService,
  LeadStatusDto,
  LeadStatusPayload,
  LeadSubStatusDto,
  WorkflowGraphData,
} from '../../../shared/services/lead-statuses.service';
import {
  AdminButtonComponent,
  AdminEmptyStateComponent,
  AdminModalComponent,
  AdminPageHeaderComponent,
  AdminTableComponent,
  StatusBadgeComponent,
} from '../../../shared/admin';
import { WorkflowGraphComponent } from './components/workflow-graph.component';

type StatusForm = LeadStatusPayload & { isActive: boolean; isSystem: boolean };
type SubStatusForm = {
  name: string;
  description: string;
  displayOrder: number;
  isActive: boolean;
};
type CrmTab = 'statuses' | 'sub-statuses';

@Component({
  selector: 'app-lead-statuses-page',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    DragDropModule,
    AdminPageHeaderComponent,
    AdminButtonComponent,
    AdminTableComponent,
    AdminEmptyStateComponent,
    AdminModalComponent,
    StatusBadgeComponent,
    WorkflowGraphComponent,
  ],
  template: `
    <section>
      <app-admin-page-header>
        <ng-container actions>
          @if (activeTab() === 'statuses') {
            <app-admin-button variant="secondary" type="button" (click)="openAllTransitions()">
              View Workflow
            </app-admin-button>
            @if (showCreate()) {
              <app-admin-button variant="primary" type="button" (click)="openCreate()" [disabled]="!canCreate()">Add Status</app-admin-button>
            }
          } @else {
            @if (showCreate()) {
              <app-admin-button variant="primary" type="button" (click)="openSubStatusAdd()" [disabled]="!canCreate()">Add Sub Status</app-admin-button>
            }
          }
        </ng-container>
      </app-admin-page-header>

      <nav class="cg-admin-tabs" aria-label="CRM configuration tabs">
        <button
          type="button"
          class="cg-admin-tab"
          [class.is-active]="activeTab() === 'statuses'"
          (click)="setTab('statuses')"
        >
          Status
        </button>
        <button
          type="button"
          class="cg-admin-tab"
          [class.is-active]="activeTab() === 'sub-statuses'"
          (click)="setTab('sub-statuses')"
        >
          Sub Status
        </button>
      </nav>

      @if (activeTab() === 'statuses') {
        <app-admin-table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Display Order</th>
              <th>Active</th>
              <th>System</th>
              <th style="text-align: right">Actions</th>
            </tr>
          </thead>
          <tbody>
            @if (list().length === 0) {
              <tr>
                <td colspan="6">
                  <app-admin-empty-state icon="inbox" [title]="emptyTitle" />
                </td>
              </tr>
            }
            @for (s of list(); track s._id) {
              <tr>
                <td><strong>{{ s.name }}</strong></td>
                <td><code class="cg-input--mono">{{ s.code }}</code></td>
                <td>{{ s.displayOrder }}</td>
                <td>
                  <app-status-badge [variant]="s.isActive ? 'published' : 'draft'">
                    {{ s.isActive ? 'Active' : 'Inactive' }}
                  </app-status-badge>
                </td>
                <td>
                  @if (s.isSystem) {
                    <app-status-badge variant="published">System</app-status-badge>
                  } @else {
                    <span class="text-slate-500">—</span>
                  }
                </td>
                <td>
                  <div class="cg-admin-table-actions">
                    @if (showEdit()) {
                      <app-admin-button variant="ghost" type="button" (click)="edit(s)" [disabled]="!canEdit()">Edit</app-admin-button>
                    }
                    @if (showManageTransitions()) {
                      <app-admin-button
                        variant="secondary"
                        type="button"
                        [routerLink]="canManageTransitions() ? [listRoute, s._id, 'transitions'] : null"
                        [disabled]="!canManageTransitions()"
                      >
                        Configure Transitions
                      </app-admin-button>
                    }
                    @if (!s.isSystem && showDelete()) {
                      <app-admin-button variant="danger" type="button" (click)="remove(s)" [disabled]="!canDelete()">Delete</app-admin-button>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </app-admin-table>
      } @else {
        @if (subStatusList().length === 0) {
          <app-admin-empty-state
            icon="inbox"
            [title]="subStatusEmptyTitle"
            description="Add sub statuses to track detailed progress independently of main status."
          />
        } @else {
          <p class="sub-status-help text-sm text-slate-600">
            Drag the <strong>⋮⋮</strong> handle on a row to change order. Order is saved automatically.
          </p>
          <div class="sub-status-header" aria-hidden="true">
            <span></span>
            <span>Name</span>
            <span>Order</span>
            <span>Active</span>
            <span>System</span>
            <span></span>
          </div>
          <ul
            class="sub-status-list"
            cdkDropList
            [cdkDropListData]="subStatusList()"
            (cdkDropListDropped)="onSubStatusDrop($event)"
          >
            @for (row of subStatusList(); track row._id; let i = $index) {
              <li class="sub-status-row" cdkDrag>
                @if (showReorder()) {
                  <span class="sub-status-row__handle" cdkDragHandle title="Drag to reorder" aria-label="Drag to reorder">⋮⋮</span>
                } @else {
                  <span class="sub-status-row__handle" aria-hidden="true"></span>
                }
                <div class="sub-status-row__name">
                  <span class="font-semibold text-slate-900">{{ row.name }}</span>
                </div>
                <span class="sub-status-order">{{ i + 1 }}</span>
                <app-status-badge [variant]="row.isActive ? 'published' : 'draft'">
                  {{ row.isActive ? 'Active' : 'Inactive' }}
                </app-status-badge>
                <span>
                  @if (row.isSystem) {
                    <app-status-badge variant="published">System</app-status-badge>
                  } @else {
                    <span class="text-slate-500">—</span>
                  }
                </span>
                <div class="sub-status-row__actions">
                  @if (showEdit()) {
                    <app-admin-button variant="ghost" type="button" (click)="editSubStatus(row)" [disabled]="!canEdit()">Edit</app-admin-button>
                  }
                  @if (!row.isSystem && showDelete()) {
                    <app-admin-button variant="danger" type="button" (click)="removeSubStatus(row)" [disabled]="!canDelete()">Delete</app-admin-button>
                  }
                </div>
              </li>
            }
          </ul>
        }
      }

      @if (showAllTransitions()) {
        <app-admin-modal [title]="workflowModalTitle" size="xl" (closed)="closeAllTransitions()">
          @if (loadingAllTransitions()) {
            <p class="text-sm text-slate-600">Loading workflow…</p>
          } @else if (workflowGraph().statuses.length === 0 || workflowGraph().transitions.length === 0) {
            <app-admin-empty-state icon="inbox" title="No workflow transitions configured" />
          } @else {
            <app-workflow-graph
              [statuses]="workflowGraph().statuses"
              [transitions]="workflowGraph().transitions"
            />
          }
          <div class="cg-admin-page-header__actions" style="margin-top: var(--cg-space-5); justify-content: flex-end">
            <app-admin-button variant="cancel" type="button" (click)="closeAllTransitions()">Close</app-admin-button>
          </div>
        </app-admin-modal>
      }

      @if (showForm()) {
        <app-admin-modal [title]="editingId() ? 'Edit status' : 'Add status'" size="lg" (closed)="closeForm()">
          <div class="cg-admin-form-grid cg-admin-form-grid--2">
            <label class="block text-sm">
              <span class="text-slate-700">Code</span>
              <input
                class="mt-1 w-full rounded border p-2 font-mono"
                [(ngModel)]="form.code"
                name="code"
                [disabled]="!!editingId()"
              />
            </label>
            <label class="block text-sm">
              <span class="text-slate-700">Name</span>
              <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.name" name="name" />
            </label>
            <label class="block text-sm sm:col-span-2">
              <span class="text-slate-700">Description</span>
              <textarea class="mt-1 w-full rounded border p-2" rows="2" [(ngModel)]="form.description" name="description"></textarea>
            </label>
            <label class="block text-sm">
              <span class="text-slate-700">Display Order</span>
              <input type="number" class="mt-1 w-full rounded border p-2" [(ngModel)]="form.displayOrder" name="displayOrder" />
            </label>
            <label class="block text-sm flex items-center gap-2" style="align-self: end">
              <input type="checkbox" [(ngModel)]="form.isActive" name="isActive" [disabled]="form.isSystem" />
              <span class="text-slate-700">Active</span>
            </label>
          </div>
          @if (error(); as msg) {
            <p class="mt-3 text-sm text-red-600">{{ msg }}</p>
          }
          <div class="cg-admin-page-header__actions" style="margin-top: var(--cg-space-5); justify-content: flex-end">
            <app-admin-button variant="cancel" type="button" (click)="closeForm()">Cancel</app-admin-button>
            @if (showSaveStatus()) {
              <app-admin-button variant="accent" type="button" (click)="save()" [disabled]="saving()">
                {{ saving() ? 'Saving...' : 'Save' }}
              </app-admin-button>
            }
          </div>
        </app-admin-modal>
      }

      @if (showSubStatusForm()) {
        <app-admin-modal
          [title]="editingSubStatusId() ? 'Edit sub status' : 'Add sub status'"
          size="lg"
          (closed)="closeSubStatusForm()"
        >
          <div class="cg-admin-form-grid cg-admin-form-grid--2">
            <label class="block text-sm sm:col-span-2">
              <span class="text-slate-700">Name *</span>
              <input class="mt-1 w-full rounded border p-2" [(ngModel)]="subStatusForm.name" name="subStatusName" />
            </label>
            <label class="block text-sm sm:col-span-2">
              <span class="text-slate-700">Description</span>
              <textarea
                class="mt-1 w-full rounded border p-2"
                rows="2"
                [(ngModel)]="subStatusForm.description"
                name="subStatusDescription"
              ></textarea>
            </label>
            <label class="block text-sm flex items-center gap-2 sm:col-span-2">
              <input
                type="checkbox"
                [(ngModel)]="subStatusForm.isActive"
                name="subStatusIsActive"
                [disabled]="editingSubStatusIsSystem()"
              />
              <span class="text-slate-700">Active</span>
            </label>
          </div>
          @if (subStatusError(); as msg) {
            <p class="mt-3 text-sm text-red-600">{{ msg }}</p>
          }
          <div class="cg-admin-page-header__actions" style="margin-top: var(--cg-space-5); justify-content: flex-end">
            <app-admin-button variant="cancel" type="button" (click)="closeSubStatusForm()">Cancel</app-admin-button>
            @if (showSaveSubStatus()) {
              <app-admin-button variant="accent" type="button" (click)="saveSubStatus()" [disabled]="subStatusSaving()">
                {{ subStatusSaving() ? 'Saving...' : 'Save' }}
              </app-admin-button>
            }
          </div>
        </app-admin-modal>
      }
    </section>
  `,
  styles: `
    .cg-admin-tabs {
      display: flex;
      gap: var(--cg-space-2);
      margin-bottom: var(--cg-space-5);
      border-bottom: 1px solid var(--cg-color-border-subtle, #e2e8f0);
    }

    .cg-admin-tab {
      appearance: none;
      background: none;
      border: none;
      border-bottom: 2px solid transparent;
      color: var(--cg-color-text-muted, #64748b);
      cursor: pointer;
      font-size: 0.875rem;
      font-weight: 600;
      margin-bottom: -1px;
      padding: 0.65rem 0.9rem;
    }

    .cg-admin-tab.is-active {
      border-bottom-color: var(--cg-color-primary, #2563eb);
      color: var(--cg-color-text, #0f172a);
    }

    .sub-status-help {
      margin: 0 0 var(--cg-space-4);
    }

    .sub-status-header {
      display: grid;
      grid-template-columns: auto 1fr 4rem 6rem 6rem auto;
      gap: var(--cg-space-3);
      padding: 0 var(--cg-space-4) var(--cg-space-2);
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--cg-color-text-muted, #64748b);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .sub-status-list {
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-3);
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .sub-status-row {
      display: grid;
      grid-template-columns: auto 1fr 4rem 6rem 6rem auto;
      align-items: center;
      gap: var(--cg-space-3);
      padding: var(--cg-space-3) var(--cg-space-4);
      border: 1px solid var(--cg-color-border-subtle, #e2e8f0);
      border-radius: var(--cg-radius-md, 0.5rem);
      background: var(--cg-color-surface, #fff);
    }

    .sub-status-row__handle {
      cursor: grab;
      color: var(--cg-color-text-muted, #64748b);
      user-select: none;
      font-size: 0.875rem;
      letter-spacing: -0.15em;
    }

    .sub-status-row__name {
      display: flex;
      align-items: center;
      gap: var(--cg-space-2);
      min-width: 0;
    }

    .sub-status-order {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--cg-color-text-secondary, #475569);
      text-align: center;
    }

    .sub-status-row__actions {
      display: flex;
      flex-wrap: wrap;
      gap: var(--cg-space-2);
      justify-content: flex-end;
    }

    .cdk-drag-preview {
      box-sizing: border-box;
      border-radius: var(--cg-radius-md, 0.5rem);
      box-shadow: var(--cg-shadow-md, 0 4px 12px rgba(15, 23, 42, 0.12));
    }

    .cdk-drag-placeholder {
      opacity: 0.35;
    }

    @media (max-width: 1024px) {
      .sub-status-header {
        display: none;
      }

      .sub-status-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--cg-space-2) var(--cg-space-3);
      }

      .sub-status-row__name {
        flex: 1 1 10rem;
      }

      .sub-status-order {
        text-align: left;
      }

      .sub-status-row__actions {
        flex: 1 1 100%;
        justify-content: flex-start;
      }
    }
  `,
})
export class LeadStatusesPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(LeadStatusesService);
  private readonly permissions = inject(PermissionService);

  readonly crmFeatures: CrmFeature = this.route.snapshot.data['crmFeatures'] ?? 'leads';

  private statusModuleKey(): string {
    return this.crmFeatures === 'applicants' ? 'crm.applicantStatuses' : 'crm.leadStatuses';
  }

  readonly showCreate = computed(() => this.permissions.canShow(this.statusModuleKey(), 'create'));
  readonly canCreate = computed(() => this.permissions.canExecute(this.statusModuleKey(), 'create'));
  readonly showEdit = computed(() => this.permissions.canShow(this.statusModuleKey(), 'edit'));
  readonly canEdit = computed(() => this.permissions.canExecute(this.statusModuleKey(), 'edit'));
  readonly showDelete = computed(() => this.permissions.canShow(this.statusModuleKey(), 'delete'));
  readonly canDelete = computed(() => this.permissions.canExecute(this.statusModuleKey(), 'delete'));
  readonly showReorder = computed(() => this.permissions.canShow(this.statusModuleKey(), 'reorder'));
  readonly canReorder = computed(() => this.permissions.canExecute(this.statusModuleKey(), 'reorder'));
  readonly showManageTransitions = computed(() =>
    this.permissions.canShow(this.statusModuleKey(), 'manageTransitions'),
  );
  readonly canManageTransitions = computed(() =>
    this.permissions.canExecute(this.statusModuleKey(), 'manageTransitions'),
  );
  readonly showSaveStatus = computed(() =>
    this.editingId() ? this.showEdit() : this.showCreate(),
  );
  readonly showSaveSubStatus = computed(() =>
    this.editingSubStatusId() ? this.showEdit() : this.showCreate(),
  );
  readonly canSaveStatus = computed(() =>
    this.editingId() ? this.canEdit() : this.canCreate(),
  );
  readonly canSaveSubStatus = computed(() =>
    this.editingSubStatusId() ? this.canEdit() : this.canCreate(),
  );
  readonly listRoute =
    this.crmFeatures === 'applicants' ? '/platform/crm/applicant-statuses' : '/platform/crm/lead-statuses';
  readonly entityLabel = this.crmFeatures === 'applicants' ? 'applicant' : 'lead';
  readonly emptyTitle = `No ${this.entityLabel} statuses yet`;
  readonly subStatusEmptyTitle = `No ${this.entityLabel} sub statuses yet`;
  readonly workflowModalTitle = `${this.entityLabel.charAt(0).toUpperCase()}${this.entityLabel.slice(1)} Workflow`;

  readonly activeTab = signal<CrmTab>('statuses');
  readonly list = signal<LeadStatusDto[]>([]);
  readonly subStatusList = signal<LeadSubStatusDto[]>([]);
  readonly showForm = signal(false);
  readonly showSubStatusForm = signal(false);
  readonly showAllTransitions = signal(false);
  readonly loadingAllTransitions = signal(false);
  readonly workflowGraph = signal<WorkflowGraphData>({ statuses: [], transitions: [] });
  readonly editingId = signal<string | null>(null);
  readonly editingSubStatusId = signal<string | null>(null);
  readonly editingSubStatusIsSystem = signal(false);
  readonly saving = signal(false);
  readonly subStatusSaving = signal(false);
  readonly error = signal<string | null>(null);
  readonly subStatusError = signal<string | null>(null);

  form: StatusForm = this.emptyForm();
  subStatusForm: SubStatusForm = this.emptySubStatusForm();

  constructor() {
    this.refresh();
  }

  setTab(tab: CrmTab): void {
    this.activeTab.set(tab);
    if (tab === 'sub-statuses') {
      this.refreshSubStatuses();
    }
  }

  private emptyForm(): StatusForm {
    return {
      code: '',
      name: '',
      description: '',
      displayOrder: 0,
      isActive: true,
      isSystem: false,
    };
  }

  private emptySubStatusForm(): SubStatusForm {
    return {
      name: '',
      description: '',
      displayOrder: this.subStatusList().length + 1,
      isActive: true,
    };
  }

  refresh(): void {
    this.api.list(this.crmFeatures).subscribe((items) => this.list.set(items));
  }

  refreshSubStatuses(): void {
    this.api.listSubStatuses(this.crmFeatures).subscribe((items) => {
      const sorted = this.sortSubStatuses(items);
      if (this.subStatusesNeedNormalize(sorted)) {
        const orderedIds = sorted.map((row) => row._id);
        this.api.reorderSubStatuses(orderedIds, this.crmFeatures).subscribe({
          next: (rows) => this.subStatusList.set(this.sortSubStatuses(rows)),
          error: () => this.subStatusList.set(sorted),
        });
        return;
      }
      this.subStatusList.set(sorted);
    });
  }

  private sortSubStatuses(items: LeadSubStatusDto[]): LeadSubStatusDto[] {
    return [...items].sort((a, b) => {
      if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
      return a.name.localeCompare(b.name);
    });
  }

  private subStatusesNeedNormalize(items: LeadSubStatusDto[]): boolean {
    if (items.length <= 1) return false;
    const orders = items.map((row) => row.displayOrder);
    if (new Set(orders).size !== orders.length) return true;
    return orders.some((order, index) => order !== index + 1);
  }

  openCreate(): void {
    this.form = this.emptyForm();
    this.editingId.set(null);
    this.error.set(null);
    this.showForm.set(true);
  }

  openSubStatusAdd(): void {
    this.subStatusForm = {
      ...this.emptySubStatusForm(),
      displayOrder: this.subStatusList().length + 1,
    };
    this.editingSubStatusId.set(null);
    this.editingSubStatusIsSystem.set(false);
    this.subStatusError.set(null);
    this.showSubStatusForm.set(true);
  }

  openAllTransitions(): void {
    this.showAllTransitions.set(true);
    this.loadingAllTransitions.set(true);
    this.workflowGraph.set({ statuses: [], transitions: [] });
    this.api.listWorkflowGraph(this.crmFeatures).subscribe({
      next: (graph) => {
        this.workflowGraph.set(graph);
        this.loadingAllTransitions.set(false);
      },
      error: () => {
        this.loadingAllTransitions.set(false);
        this.workflowGraph.set({ statuses: [], transitions: [] });
      },
    });
  }

  closeAllTransitions(): void {
    this.showAllTransitions.set(false);
  }

  edit(s: LeadStatusDto): void {
    this.form = {
      code: s.code,
      name: s.name,
      description: s.description ?? '',
      displayOrder: s.displayOrder,
      isActive: s.isActive,
      isSystem: s.isSystem,
    };
    this.editingId.set(s._id);
    this.error.set(null);
    this.showForm.set(true);
  }

  editSubStatus(row: LeadSubStatusDto): void {
    this.subStatusForm = {
      name: row.name,
      description: row.description ?? '',
      displayOrder: row.displayOrder,
      isActive: row.isActive,
    };
    this.editingSubStatusId.set(row._id);
    this.editingSubStatusIsSystem.set(row.isSystem);
    this.subStatusError.set(null);
    this.showSubStatusForm.set(true);
  }

  remove(s: LeadStatusDto): void {
    if (s.isSystem) return;
    if (!confirm(`Delete status ${s.name}?`)) return;
    this.api.remove(s._id, this.crmFeatures).subscribe({
      next: () => this.refresh(),
      error: (err) => alert(err?.error?.message ?? 'Delete failed.'),
    });
  }

  removeSubStatus(row: LeadSubStatusDto): void {
    if (row.isSystem) return;
    if (!confirm(`Delete sub status "${row.name}"?`)) return;
    this.api.removeSubStatus(row._id, this.crmFeatures).subscribe({
      next: () => this.refreshSubStatuses(),
      error: (err) => alert(err?.error?.message ?? 'Delete failed.'),
    });
  }

  closeForm(): void {
    this.showForm.set(false);
  }

  closeSubStatusForm(): void {
    this.showSubStatusForm.set(false);
  }

  save(): void {
    if (!this.form.code.trim() || !this.form.name.trim()) {
      this.error.set('Code and name are required.');
      return;
    }

    this.saving.set(true);
    this.error.set(null);
    const id = this.editingId();

    const req$ = id
      ? this.api.update(
          id,
          {
            name: this.form.name,
            description: this.form.description || undefined,
            displayOrder: Number(this.form.displayOrder) || 0,
            isActive: this.form.isActive,
          },
          this.crmFeatures,
        )
      : this.api.create(
          {
            code: this.form.code,
            name: this.form.name,
            description: this.form.description || undefined,
            displayOrder: Number(this.form.displayOrder) || 0,
          },
          this.crmFeatures,
        );

    req$.subscribe({
      next: () => {
        this.saving.set(false);
        this.showForm.set(false);
        this.refresh();
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(err?.error?.message ?? 'Save failed.');
      },
    });
  }

  saveSubStatus(): void {
    if (!this.subStatusForm.name.trim()) {
      this.subStatusError.set('Name is required.');
      return;
    }

    this.subStatusSaving.set(true);
    this.subStatusError.set(null);
    const id = this.editingSubStatusId();
    const basePayload = {
      name: this.subStatusForm.name.trim(),
      description: this.subStatusForm.description.trim() || undefined,
      isActive: this.subStatusForm.isActive,
    };

    const req$ = id
      ? this.api.updateSubStatus(id, basePayload, this.crmFeatures)
      : this.api.createSubStatus(
          { ...basePayload, displayOrder: this.subStatusList().length + 1 },
          this.crmFeatures,
        );

    req$.subscribe({
      next: () => {
        this.subStatusSaving.set(false);
        this.showSubStatusForm.set(false);
        this.refreshSubStatuses();
      },
      error: (err) => {
        this.subStatusSaving.set(false);
        this.subStatusError.set(err?.error?.message ?? 'Save failed.');
      },
    });
  }

  onSubStatusDrop(event: CdkDragDrop<LeadSubStatusDto[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    const items = [...this.subStatusList()];
    moveItemInArray(items, event.previousIndex, event.currentIndex);
    this.subStatusList.set(items);

    const orderedIds = items.map((row) => row._id);
    this.api.reorderSubStatuses(orderedIds, this.crmFeatures).subscribe({
      next: (rows) => this.subStatusList.set(this.sortSubStatuses(rows)),
      error: () => this.refreshSubStatuses(),
    });
  }
}
